import { Action, Reducer } from 'redux';
import { AppThunkAction } from '..';
import { getRequestOptions, postRequestOptions } from '../../utils/utils';
import {
  CommandHistoryRecord,
  GroupedCommandRecord,
} from '../../Interfaces/Commands/CommandRecordInfo';
import { FetchError } from '../../Interfaces/FetchError';
import { ApiEnums } from '../../Interfaces/ApiEnums';
import { GroupedScheduledCommandRecord, ScheduledCommandHistoryRecord } from '../../Interfaces/Commands/ScheduledCommandRecordInfo';

export enum CommandsActionTypes {
  REQUEST_COMMANDS_BY_BATCH_LIST = 'REQUEST_COMMANDS_BY_BATCH_LIST',
  RECEIVE_COMMANDS_BY_BATCH_LIST = 'RECEIVE_COMMANDS_BY_BATCH_LIST',
  REQUEST_SCHEDULED_COMMANDS_BY_BATCH_LIST = 'REQUEST_SCHEDULED_COMMANDS_BY_BATCH_LIST',
  RECEIVE_SCHEDULED_COMMANDS_BY_BATCH_LIST = 'RECEIVE_SCHEDULED_COMMANDS_BY_BATCH_LIST',
  REQUEST_COMMANDS_BY_BATCH_ID = 'REQUEST_COMMANDS_BY_BATCH_ID',
  RECEIVE_COMMANDS_BY_BATCH_ID = 'RECEIVE_COMMANDS_BY_BATCH_ID',
  REQUEST_SCHEDULED_COMMANDS_BY_BATCH_ID = 'REQUEST_SCHEDULED_COMMANDS_BY_BATCH_ID',
  RECEIVE_SCHEDULED_COMMANDS_BY_BATCH_ID = 'RECEIVE_SCHEDULED_COMMANDS_BY_BATCH_ID',

  REQUEST_ALL_CONFIG_COMMANDS = 'REQUEST_ALL_CONFIG_COMMANDS',
  RECEIVE_ALL_CONFIG_COMMANDS = 'RECEIVE_ALL_CONFIG_COMMANDS',

  REQUEST_CONFIG_COMMANDS_BY_BATCH_ID = 'REQUEST_CONFIG_COMMANDS_BY_BATCH_ID',
  RECEIVE_CONFIG_COMMANDS_BY_BATCH_ID = 'RECEIVE_CONFIG_COMMANDS_BY_BATCH_ID',

  REQUEST_COMMANDS_BY_DATE_RANGE = 'REQUEST_COMMANDS_BY_DATE_RANGE',
  RECEIVE_COMMANDS_BY_DATE_RANGE = 'RECEIVE_COMMANDS_BY_DATE_RANGE',
  REQUEST_SCHEDULED_COMMANDS_BY_DATE_RANGE = 'REQUEST_SCHEDULED_COMMANDS_BY_DATE_RANGE',
  RECEIVE_SCHEDULED_COMMANDS_BY_DATE_RANGE = 'RECEIVE_SCHEDULED_COMMANDS_BY_DATE_RANGE',
  UPDATE_COMMAND_BY_ID = 'UPDATE_COMMAND_BY_ID',
  UPDATE_SCHEDULED_COMMAND_BY_ID = 'UPDATE_SCHEDULED_COMMAND_BY_ID',
  UPDATE_COMMAND_BY_BATCH_ID = 'UPDATE_COMMAND_BY_BATCH_ID',
  UPDATE_SCHEDULED_COMMAND_BY_BATCH_ID = 'UPDATE_SCHEDULED_COMMAND_BY_BATCH_ID',
  RECEIVED_COMMAND_HTTP_RESPONSE_ERROR_CODE = 'RECEIVED_NBC_HTTP_RESPONSE_ERROR_CODE',
  RESET_HTTP_RESPONSE_FETCH_ERROR = 'RESET_HTTP_RESPONSE_FETCH_ERROR',
  RESET_NOTIFICATION_MESSAGE = 'RESET_NOTIFICATION_MESSAGE',

  REQUEST_ALL_NBC_COMMANDS = 'REQUEST_ALL_NBC_COMMANDS',
  RECEIVE_ALL_NBC_COMMANDS = 'RECEIVE_ALL_NBC_COMMANDS',
  REQUEST_NBC_COMMANDS_BY_BATCH_ID = 'REQUEST_NCB_COMMANDS_BY_BATCH_ID',
  RECEIVE_NBC_COMMANDS_BY_BATCH_ID = 'RECEIVE_NBC_COMMANDS_BY_BATCH_ID',
}
// -----------------
// STATE - This defines the type of data maintained in the Redux store.
export interface CommandsState {
  commandList: GroupedCommandRecord[];
  scheduledCommandList: GroupedScheduledCommandRecord[];
  commandListByBatchID: CommandHistoryRecord[];
  scheduledCommandListByBatchID: ScheduledCommandHistoryRecord[];
  isLoadingCommands: boolean;
  commandListByDateRange: GroupedCommandRecord[];
  scheduledCommandListByDateRange: GroupedScheduledCommandRecord[];
  notificationMessage: string;
  fetchError: FetchError;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.

interface RequestCommandByBatchListAction {
  type: CommandsActionTypes.REQUEST_COMMANDS_BY_BATCH_LIST;
}

interface ReceivedCommandByBatchListAction {
  type: CommandsActionTypes.RECEIVE_COMMANDS_BY_BATCH_LIST;
  commandList: GroupedCommandRecord[];
}

interface RequestScheduledCommandByBatchListAction {
  type: CommandsActionTypes.REQUEST_SCHEDULED_COMMANDS_BY_BATCH_LIST;
}

interface ReceivedScheduledCommandByBatchListAction {
  type: CommandsActionTypes.RECEIVE_SCHEDULED_COMMANDS_BY_BATCH_LIST;
  commandList: GroupedScheduledCommandRecord[];
}

interface RequestCommandListByID {
  type: CommandsActionTypes.REQUEST_COMMANDS_BY_BATCH_ID;
}

interface ReceiveCommandListByID {
  type: CommandsActionTypes.RECEIVE_COMMANDS_BY_BATCH_ID;
  commandList: CommandHistoryRecord[];
}

interface RequestScheduledCommandListByID {
  type: CommandsActionTypes.REQUEST_SCHEDULED_COMMANDS_BY_BATCH_ID;
}

interface ReceiveScheduledCommandListByID {
  type: CommandsActionTypes.RECEIVE_SCHEDULED_COMMANDS_BY_BATCH_ID;
  commandList: ScheduledCommandHistoryRecord[];
}

interface RequestAllConfigCommands {
  type: CommandsActionTypes.REQUEST_ALL_CONFIG_COMMANDS;
}

interface ReceivedAllConfigCommands {
  type: CommandsActionTypes.RECEIVE_ALL_CONFIG_COMMANDS;
  commandList: GroupedCommandRecord[];
}

interface RequestConfigCommandsByBatchID {
  type: CommandsActionTypes.REQUEST_CONFIG_COMMANDS_BY_BATCH_ID;
}

interface ReceivedConfigCommandsByBatchID {
  type: CommandsActionTypes.RECEIVE_CONFIG_COMMANDS_BY_BATCH_ID;
  record: CommandHistoryRecord[];
}

interface RequestCommandByDateRange {
  type: CommandsActionTypes.REQUEST_COMMANDS_BY_DATE_RANGE;
}

interface RecieveCommandByDateRange {
  type: CommandsActionTypes.RECEIVE_COMMANDS_BY_DATE_RANGE;
  commandList: CommandHistoryRecord[];
}

interface RequestScheduledCommandByDateRange {
  type: CommandsActionTypes.REQUEST_SCHEDULED_COMMANDS_BY_DATE_RANGE;
}

interface RecieveScheduledCommandByDateRange {
  type: CommandsActionTypes.RECEIVE_SCHEDULED_COMMANDS_BY_DATE_RANGE;
  commandList: ScheduledCommandHistoryRecord[];
}

interface UpdateCommandById {
  type: CommandsActionTypes.UPDATE_COMMAND_BY_ID;
}

interface UpdateScheduledCommandById {
  type: CommandsActionTypes.UPDATE_SCHEDULED_COMMAND_BY_ID;
}

interface UpdateCommandByBatchId {
  type: CommandsActionTypes.UPDATE_COMMAND_BY_BATCH_ID;
}

interface UpdateScheduledCommandByBatchId {
  type: CommandsActionTypes.UPDATE_SCHEDULED_COMMAND_BY_BATCH_ID;
}

interface ReceivedErrorCode {
  type: CommandsActionTypes.RECEIVED_COMMAND_HTTP_RESPONSE_ERROR_CODE;
  statusCode: number;
}

interface ResetHttpResponseError {
  type: CommandsActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR;
}

interface ResetNotificationMessage {
  type: CommandsActionTypes.RESET_NOTIFICATION_MESSAGE;
}

interface RequestAllNBCCommands {
  type: CommandsActionTypes.REQUEST_ALL_NBC_COMMANDS;
}

interface ReceivedAllNBCCommands {
  type: CommandsActionTypes.RECEIVE_ALL_NBC_COMMANDS;
  commandList: GroupedCommandRecord[];
}

interface RequestNBCCommandsByBatchID {
  type: CommandsActionTypes.REQUEST_NBC_COMMANDS_BY_BATCH_ID;
}

interface ReceivedNBCCommandsByBatchID {
  type: CommandsActionTypes.RECEIVE_NBC_COMMANDS_BY_BATCH_ID;
  record: CommandHistoryRecord[];
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | RequestCommandByBatchListAction
  | ReceivedCommandByBatchListAction
  | RequestScheduledCommandByBatchListAction
  | ReceivedScheduledCommandByBatchListAction
  | RequestCommandListByID
  | ReceiveCommandListByID
  | RequestScheduledCommandListByID
  | ReceiveScheduledCommandListByID
  | RequestAllConfigCommands
  | ReceivedAllConfigCommands
  | RequestConfigCommandsByBatchID
  | ReceivedConfigCommandsByBatchID
  | RequestCommandByDateRange
  | RecieveCommandByDateRange
  | RequestScheduledCommandByDateRange
  | RecieveScheduledCommandByDateRange
  | UpdateCommandById
  | UpdateScheduledCommandById
  | UpdateCommandByBatchId
  | UpdateScheduledCommandByBatchId
  | ReceivedErrorCode
  | ResetHttpResponseError
  | ResetNotificationMessage
  | RequestAllNBCCommands
  | ReceivedAllNBCCommands
  | RequestNBCCommandsByBatchID
  | ReceivedNBCCommandsByBatchID;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).
const isResponseOk = (response: Response, dispatch: any): boolean => {
  if (response.ok) return true;
  if (response.status !== 401) {
    dispatch({
      type: CommandsActionTypes.RECEIVED_COMMAND_HTTP_RESPONSE_ERROR_CODE,
      statusCode: response.status,
    });
  }

  return false;
};

export const actionCreators = {
  getGroupedCommandsByBatchID:
    (command: string, startDate: string, endDate: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${CommandsActionTypes.REQUEST_COMMANDS_BY_BATCH_LIST}`
      );

      dispatch({
        type: CommandsActionTypes.REQUEST_COMMANDS_BY_BATCH_LIST,
      });

      const response = await fetch(
        `CommandResponse/GetGroupedCommandsByBatchID/${command}/${startDate}/${endDate}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as GroupedCommandRecord[];

      console.log(
        `Dispatching ${CommandsActionTypes.RECEIVE_COMMANDS_BY_BATCH_LIST}`
      );

      dispatch({
        type: CommandsActionTypes.RECEIVE_COMMANDS_BY_BATCH_LIST,
        commandList: result,
      });
    },

  getGroupedScheduledCommandsByBatchID:
    (command: string, startDate: string, endDate: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${CommandsActionTypes.REQUEST_SCHEDULED_COMMANDS_BY_BATCH_LIST}`
      );

      dispatch({
        type: CommandsActionTypes.REQUEST_SCHEDULED_COMMANDS_BY_BATCH_LIST,
      });

      const response = await fetch(
        `CommandResponse/GetGroupedScheduledCommandsByBatchID/${command}/${startDate}/${endDate}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as GroupedScheduledCommandRecord[];

      console.log(
        `Dispatching ${CommandsActionTypes.RECEIVE_SCHEDULED_COMMANDS_BY_BATCH_LIST}`
      );

      dispatch({
        type: CommandsActionTypes.RECEIVE_SCHEDULED_COMMANDS_BY_BATCH_LIST,
        commandList: result,
      });
    },

  getCommandByBatchID:
    (command: string, batchID: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${CommandsActionTypes.REQUEST_COMMANDS_BY_BATCH_ID}`
      );

      dispatch({
        type: CommandsActionTypes.REQUEST_COMMANDS_BY_BATCH_ID,
      });

      const response = await fetch(
        `CommandResponse/GetCommandByBatchID/${command}/${batchID}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as CommandHistoryRecord[];

      console.log(
        `Dispatching ${CommandsActionTypes.RECEIVE_COMMANDS_BY_BATCH_ID}`
      );

      dispatch({
        type: CommandsActionTypes.RECEIVE_COMMANDS_BY_BATCH_ID,
        commandList: result,
      });
    },
  
  getScheduledCommandByBatchID:
    (command: string, batchID: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${CommandsActionTypes.REQUEST_SCHEDULED_COMMANDS_BY_BATCH_ID}`
      );

      dispatch({
        type: CommandsActionTypes.REQUEST_SCHEDULED_COMMANDS_BY_BATCH_ID,
      });

      const response = await fetch(
        `CommandResponse/GetScheduledCommandByBatchID/${command}/${batchID}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as ScheduledCommandHistoryRecord[];

      console.log(
        `Dispatching ${CommandsActionTypes.RECEIVE_SCHEDULED_COMMANDS_BY_BATCH_ID}`
      );

      dispatch({
        type: CommandsActionTypes.RECEIVE_SCHEDULED_COMMANDS_BY_BATCH_ID,
        commandList: result,
      });
    },

  getAllConfigCommands:
    (startDate: string, endDate: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${CommandsActionTypes.REQUEST_ALL_CONFIG_COMMANDS}`
      );

      dispatch({
        type: CommandsActionTypes.REQUEST_ALL_CONFIG_COMMANDS,
      });

      const response = await fetch(
        `CommandResponse/GetAllConfigCommands/${startDate}/${endDate}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as GroupedCommandRecord[];

      console.log(
        `Dispatching ${CommandsActionTypes.RECEIVE_ALL_CONFIG_COMMANDS}`
      );

      dispatch({
        type: CommandsActionTypes.RECEIVE_ALL_CONFIG_COMMANDS,
        commandList: result,
      });
    },

  getConfigCommandByBatchID:
    (command: string, batchID: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${CommandsActionTypes.REQUEST_CONFIG_COMMANDS_BY_BATCH_ID}`
      );

      dispatch({
        type: CommandsActionTypes.REQUEST_CONFIG_COMMANDS_BY_BATCH_ID,
      });

      const response = await fetch(
        `CommandResponse/getConfigCommandByBatchID/${command}/${batchID}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as CommandHistoryRecord[];

      console.log(
        `Dispatching ${CommandsActionTypes.RECEIVE_CONFIG_COMMANDS_BY_BATCH_ID}`
      );

      dispatch({
        type: CommandsActionTypes.RECEIVE_CONFIG_COMMANDS_BY_BATCH_ID,
        record: result,
      });
    },

  clearCommandsByBatchID:
    (command: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: CommandsActionTypes.RECEIVE_COMMANDS_BY_BATCH_ID,
        commandList: [],
      });
    },

  getCommandByDateRange:
    (
      command: string,
      startDate: string,
      endDate: string
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: CommandsActionTypes.REQUEST_COMMANDS_BY_DATE_RANGE,
      });

      console.log(
        `Dispatching ${CommandsActionTypes.REQUEST_COMMANDS_BY_DATE_RANGE}`
      );

      const response = await fetch(
        `CommandResponse/GetCommandByDateRange/${command}/${startDate}/${endDate}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as CommandHistoryRecord[];

      console.log(
        `Dispatching ${CommandsActionTypes.RECEIVE_COMMANDS_BY_DATE_RANGE}`
      );

      dispatch({
        type: CommandsActionTypes.RECEIVE_COMMANDS_BY_DATE_RANGE,
        commandList: result,
      });
    },

  getScheduledCommandByDateRange:
    (
      command: string,
      startDate: string,
      endDate: string
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: CommandsActionTypes.REQUEST_SCHEDULED_COMMANDS_BY_DATE_RANGE,
      });

      console.log(
        `Dispatching ${CommandsActionTypes.REQUEST_SCHEDULED_COMMANDS_BY_DATE_RANGE}`
      );

      const response = await fetch(
        `CommandResponse/GetScheduledCommandByDateRange/${command}/${startDate}/${endDate}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as ScheduledCommandHistoryRecord[];

      console.log(
        `Dispatching ${CommandsActionTypes.RECEIVE_SCHEDULED_COMMANDS_BY_DATE_RANGE}`
      );

      dispatch({
        type: CommandsActionTypes.RECEIVE_SCHEDULED_COMMANDS_BY_DATE_RANGE,
        commandList: result,
      });
    },

  updateCommandById:
    (
      commandId: number,
      commandStatus: ApiEnums.CommandStatus
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(`Dispatching ${CommandsActionTypes.UPDATE_COMMAND_BY_ID}`);

      const response = await fetch(
        `CommandResponse/UpdateCommandById/${commandStatus}`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          commandId
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      dispatch({
        type: CommandsActionTypes.UPDATE_COMMAND_BY_ID,
      });
    },
  
  updateScheduledCommandById:
    (
      commandId: number,
      commandStatus: ApiEnums.ScheduledCommandStatus
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(`Dispatching ${CommandsActionTypes.UPDATE_SCHEDULED_COMMAND_BY_ID}`);

      const response = await fetch(
        `CommandResponse/UpdateScheduledCommandById/${commandStatus}`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          commandId
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      dispatch({
        type: CommandsActionTypes.UPDATE_SCHEDULED_COMMAND_BY_ID,
      });
    },

  updateCommandByBatchId:
    (
      commandBatchId: any,
      commandStatus: ApiEnums.CommandStatus
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(`Dispatching ${CommandsActionTypes.UPDATE_COMMAND_BY_BATCH_ID}`);

      const response = await fetch(
        `CommandResponse/UpdateCommandByBatchId/${commandStatus}`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          commandBatchId
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      dispatch({
        type: CommandsActionTypes.UPDATE_COMMAND_BY_BATCH_ID,
      });
    },

  updateScheduledCommandByBatchId:
    (
      commandBatchId: any,
      commandStatus: ApiEnums.ScheduledCommandStatus
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(`Dispatching ${CommandsActionTypes.UPDATE_SCHEDULED_COMMAND_BY_BATCH_ID}`);

      const response = await fetch(
        `CommandResponse/UpdateScheduledCommandByBatchId/${commandStatus}`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          commandBatchId
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      dispatch({
        type: CommandsActionTypes.UPDATE_SCHEDULED_COMMAND_BY_BATCH_ID,
      });
    },

  resetCommandFetchError:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${CommandsActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR}`
      );

      dispatch({
        type: CommandsActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR,
      });
    },

  resetNotificationMessage:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${CommandsActionTypes.RESET_NOTIFICATION_MESSAGE}`
      );

      dispatch({
        type: CommandsActionTypes.RESET_NOTIFICATION_MESSAGE,
      });
    },

  getAllNBCCommands:
    (startDate: string, endDate: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: CommandsActionTypes.REQUEST_ALL_NBC_COMMANDS,
      });

      const response = await fetch(
        `CommandResponse/GetAllNBCCommands/${startDate}/${endDate}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as GroupedCommandRecord[];

      console.log(
        `Dispatching ${CommandsActionTypes.RECEIVE_ALL_NBC_COMMANDS}`
      );

      dispatch({
        type: CommandsActionTypes.RECEIVE_ALL_CONFIG_COMMANDS,
        commandList: result,
      });
    },

  getNBCCommandByBatchID:
    (command: string, batchID: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: CommandsActionTypes.REQUEST_NBC_COMMANDS_BY_BATCH_ID,
      });

      const response = await fetch(
        `CommandResponse/getNBCCommandByBatchID/${command}/${batchID}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as CommandHistoryRecord[];

      console.log(
        `Dispatching ${CommandsActionTypes.RECEIVE_NBC_COMMANDS_BY_BATCH_ID}`
      );

      dispatch({
        type: CommandsActionTypes.RECEIVE_NBC_COMMANDS_BY_BATCH_ID,
        record: result,
      });
    },
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
export const unloadedState: CommandsState = {
  commandList: [],
  scheduledCommandList: [],
  commandListByBatchID: [],
  scheduledCommandListByBatchID: [],
  isLoadingCommands: false,
  commandListByDateRange: [],
  scheduledCommandListByDateRange: [],
  notificationMessage: '',
  fetchError: { StatusCode: 0, Message: '' },
};

export const reducer: Reducer<CommandsState> = (
  state: CommandsState | undefined,
  incomingAction: Action
): CommandsState => {
  if (state === undefined) {
    return unloadedState;
  }

  const action = incomingAction as KnownAction;
  switch (action.type) {
    case CommandsActionTypes.REQUEST_COMMANDS_BY_BATCH_LIST:
      return {
        ...state,
        isLoadingCommands: true,
      };
    case CommandsActionTypes.RECEIVE_COMMANDS_BY_BATCH_LIST:
      return {
        ...state,
        commandList: action.commandList,
        isLoadingCommands: false,
      };
    case CommandsActionTypes.REQUEST_SCHEDULED_COMMANDS_BY_BATCH_LIST:
      return {
        ...state,
        isLoadingCommands: true,
      };
    case CommandsActionTypes.RECEIVE_SCHEDULED_COMMANDS_BY_BATCH_LIST:
      return {
        ...state,
        scheduledCommandList: action.commandList,
        isLoadingCommands: false,
      };
    case CommandsActionTypes.REQUEST_COMMANDS_BY_BATCH_ID:
      return {
        ...state,
        isLoadingCommands: true,
      };
    case CommandsActionTypes.RECEIVE_COMMANDS_BY_BATCH_ID:
      return {
        ...state,
        commandListByBatchID: action.commandList,
        isLoadingCommands: false,
      };
    case CommandsActionTypes.REQUEST_SCHEDULED_COMMANDS_BY_BATCH_ID:
      return {
        ...state,
        isLoadingCommands: true,
      };
    case CommandsActionTypes.RECEIVE_SCHEDULED_COMMANDS_BY_BATCH_ID:
      return {
        ...state,
        scheduledCommandListByBatchID: action.commandList,
        isLoadingCommands: false,
      };
    case CommandsActionTypes.REQUEST_ALL_CONFIG_COMMANDS:
      return {
        ...state,
        isLoadingCommands: true,
      };

    case CommandsActionTypes.RECEIVE_ALL_CONFIG_COMMANDS:
      return {
        ...state,
        commandList: action.commandList,
        isLoadingCommands: false,
      };
    case CommandsActionTypes.REQUEST_CONFIG_COMMANDS_BY_BATCH_ID:
      return {
        ...state,
        isLoadingCommands: true,
      };
    case CommandsActionTypes.RECEIVE_CONFIG_COMMANDS_BY_BATCH_ID:
      return {
        ...state,
        commandListByBatchID: action.record,
        isLoadingCommands: false,
      };
    case CommandsActionTypes.REQUEST_COMMANDS_BY_DATE_RANGE:
      return {
        ...state,
        isLoadingCommands: true,
      };
    case CommandsActionTypes.RECEIVE_COMMANDS_BY_DATE_RANGE:
      return {
        ...state,
        commandListByDateRange: action.commandList,
        isLoadingCommands: false,
      };

    case CommandsActionTypes.REQUEST_SCHEDULED_COMMANDS_BY_DATE_RANGE:
      return {
        ...state,
        isLoadingCommands: true,
      };
    case CommandsActionTypes.RECEIVE_SCHEDULED_COMMANDS_BY_DATE_RANGE:
      return {
        ...state,
        scheduledCommandListByDateRange: action.commandList,
        isLoadingCommands: false,
      };

    case CommandsActionTypes.UPDATE_COMMAND_BY_ID:
      return {
        ...state,
        notificationMessage: 'CLOSE_COMMAND_MESSAGE',
      };
    case CommandsActionTypes.UPDATE_SCHEDULED_COMMAND_BY_ID:
      return {
        ...state,
        notificationMessage: 'CLOSE_COMMAND_MESSAGE',
      };
    case CommandsActionTypes.UPDATE_COMMAND_BY_BATCH_ID:
      return {
        ...state,
        notificationMessage: 'CLOSE_COMMAND_MESSAGE',
      };
    case CommandsActionTypes.UPDATE_SCHEDULED_COMMAND_BY_BATCH_ID:
      return {
        ...state,
        notificationMessage: 'CLOSE_COMMAND_MESSAGE',
      };
    case CommandsActionTypes.RECEIVED_COMMAND_HTTP_RESPONSE_ERROR_CODE:
      let fetchErrorData = {
        StatusCode: action.statusCode,
        Message: 'FAILED_TO_FETCH_COMMANDS',
      };
      return {
        ...state,
        isLoadingCommands: false,
        fetchError: fetchErrorData,
      };
    case CommandsActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR:
      let resetData = { StatusCode: 0, Message: '' };
      return {
        ...state,
        fetchError: resetData,
      };
    case CommandsActionTypes.RESET_NOTIFICATION_MESSAGE:
      return {
        ...state,
        notificationMessage: '',
      };
    case CommandsActionTypes.REQUEST_ALL_NBC_COMMANDS:
      return {
        ...state,
        isLoadingCommands: true,
      };
    case CommandsActionTypes.RECEIVE_ALL_NBC_COMMANDS:
      return {
        ...state,
        commandList: action.commandList,
        isLoadingCommands: false,
      };
    case CommandsActionTypes.REQUEST_NBC_COMMANDS_BY_BATCH_ID:
      return {
        ...state,
        isLoadingCommands: true,
      };
    case CommandsActionTypes.RECEIVE_NBC_COMMANDS_BY_BATCH_ID:
      return {
        ...state,
        commandListByBatchID: action.record,
        isLoadingCommands: false,
      };

    default:
      break;
  }

  return state;
};
